import { Component, OnInit } from '@angular/core';
import {
    CampaignDto,
    ConfiguratorEtlService, ConfiguratorProviderService, DetailCampaignDTO,
    EngineCampaignService, EtlConf, ProviderDTO
} from '@tj/tj-dmp-lib-api';
import {ActivatedRoute, Router} from '@angular/router';
import {ConfirmationService, MessageService, SelectItem} from 'primeng/api';
import * as moment from 'moment/moment';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'dmp-campaign-detail',
    templateUrl: './campaign-detail.component.html',
    styleUrls: ['./campaign-detail.component.scss'],
    providers: [ConfirmationService],
    standalone: false
})
export class CampaignDetailComponent implements OnInit {

    readonly userTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

    breadcrumb = [
        {label: this.translate.instant('menu.marketing.label')},
        {label: this.translate.instant('menu.marketing.campaigns')},
        {label: this.translate.instant('campaign.edit.page.breadcrumbDetail')}
    ];
    description: string;
    actual: DetailCampaignDTO;
    listFlux: SelectItem[] = [];
    listType: SelectItem[] = [];
    listProvider: SelectItem[] = [];
    listProduct: SelectItem[] = [];
    listSupport: SelectItem[] = [];
    listSchedule: SelectItem[] = [];
    listDataType: SelectItem[] = [];
    get listCampaignMode(): SelectItem[] {
        const dataType = this.actual?.campaignDto?.dataType;
        const unitsLabel = this.translate.instant(dataType === CampaignDto.DataTypeEnum.ORDERS ? 'campaign.campaignMode.UNITS_MAX_ORDERS' : 'campaign.campaignMode.UNITS_MAX_CONTACTS');
        return [
            {label: this.translate.instant('campaign.campaignMode.BUDGET_MAX'), value: 'BUDGET_MAX'},
            {label: unitsLabel, value: 'UNITS_MAX'},
            {label: this.translate.instant('campaign.campaignMode.SPREAD_EXPENSE'), value: 'SPREAD_EXPENSE'}
        ];
    }
    listStatus: SelectItem[] = [];
    exportEtlNameById: Map<string, string> = new Map();
    tenant: string;
    statusEnum = CampaignDto.StatusEnum;

    constructor(
        private readonly engineCampaignService: EngineCampaignService,
        private readonly route: ActivatedRoute,
        private readonly messageService: MessageService,
        private readonly router: Router,
        private readonly providerService: ConfiguratorProviderService,
        private readonly configuratorEtlService: ConfiguratorEtlService,
        private readonly translate: TranslateService,
        private readonly confirmationService: ConfirmationService
    ) {
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedHeader());
    }

    private rebuildTranslatedHeader(): void {
        this.breadcrumb = [
            {label: this.translate.instant('menu.marketing.label')},
            {label: this.translate.instant('menu.marketing.campaigns')},
            {label: this.translate.instant('campaign.edit.page.breadcrumbDetail')}
        ];
        this.description = this.translate.instant('campaignDetail.title');
    }

  ngOnInit(): void {
      this.tenant = this.route.snapshot.paramMap.get('tenant');
      this.description = this.translate.instant('campaignDetail.title');

      this.actual = null;
      // init list
      this.listProvider = [];
      this.listDataType = [];
      this.listStatus = [];
      this.initList();
      this.providerService.getAllProvidersUsingGET(this.tenant).subscribe((res: Array<ProviderDTO>) => {
          res.forEach(p => this.listProvider.push({label: p.name, value: p.code}));
          this.configuratorEtlService.getAllEtls(this.tenant, ['EXPORT']).subscribe((etls: EtlConf[]) => {
              (etls || []).forEach(e => this.exportEtlNameById.set(e.id, e.name || e.code || e.id));
              this.initOnUpdate();
          });
      });
  }

    getExportName(exportEtlId: string | undefined): string {
        if (!exportEtlId) return '-';
        return this.exportEtlNameById.get(exportEtlId) || exportEtlId;
    }

    private initList() {
        const defaultItem: SelectItem = {label: this.translate.instant('campaignDetail.defaultOption'), value: null};
        this.listType.push(...[
            {label: this.translate.instant('campaign.type_AFFILIATE'), value: CampaignDto.TypeEnum.AFFILIATE},
            {label: this.translate.instant('campaign.type_NETWORK'), value: CampaignDto.TypeEnum.NETWORK},
            {label: this.translate.instant('campaign.type_MEDIA'), value: CampaignDto.TypeEnum.MEDIA}]);
        this.listFlux.push(...[
            {label: this.translate.instant('campaign.flux_IMPORT'), value: 'IMPORT'},
            {label: this.translate.instant('campaign.flux_EXPORT_SCENARIO'), value: 'EXPORT_SCENARIO'},
            {label: this.translate.instant('campaign.flux_EXPORT_DIRECT'), value: 'EXPORT_DIRECT'}]);
        this.listProduct.push(...[
            defaultItem,
            {label: this.translate.instant('campaign.product_FLIGHT'), value: 'FLIGHT'},
            {label: this.translate.instant('campaign.product_HOTEL'), value: 'HOTEL'},
            {label: this.translate.instant('campaign.product_CAR'), value: 'CAR'},
            {label: this.translate.instant('campaign.product_PARKING'), value: 'PARKING'},
            {label: this.translate.instant('campaign.product_ALL'), value: 'ALL'}]);
        this.listSupport.push(...[
            {label: this.translate.instant('campaign.support_API'), value: CampaignDto.SupportEnum.API},
            {label: this.translate.instant('campaign.support_SMS'), value: CampaignDto.SupportEnum.SMS},
            {label: this.translate.instant('campaign.support_FILE'), value: CampaignDto.SupportEnum.FILE},
            {label: this.translate.instant('campaignDetail.supportMail'), value: CampaignDto.SupportEnum.MAIL},
            {label: this.translate.instant('campaign.support_FORM'), value: CampaignDto.SupportEnum.FORM},
            {label: this.translate.instant('campaign.support_EMAILING'), value: CampaignDto.SupportEnum.EMAILING}]);
        this.listSchedule.push(...[{label: this.translate.instant('campaign.schedule_MANUAL'), value: CampaignDto.ScheduleEnum.MANUAL},
            {label: this.translate.instant('campaignDetail.scheduleDaily'), value: CampaignDto.ScheduleEnum.DAILY},
            {label: this.translate.instant('campaignDetail.scheduleHourly'), value: CampaignDto.ScheduleEnum.HOURLY},
            {label: this.translate.instant('campaignDetail.scheduleRealtime'), value: CampaignDto.ScheduleEnum.REALTIME}]);
        this.listDataType.push(...[
            {label: this.translate.instant('campaign.dataType.ORDERS'), value: CampaignDto.DataTypeEnum.ORDERS},
            {label: this.translate.instant('campaign.dataType.CONTACTS'), value: CampaignDto.DataTypeEnum.CONTACTS}
        ]);
        this.listStatus.push(...[
            {label: this.translate.instant('campaign.status.DRAFT'), value: CampaignDto.StatusEnum.DRAFT},
            {label: this.translate.instant('campaign.status.IN_PROGRESS'), value: CampaignDto.StatusEnum.INPROGRESS},
            {label: this.translate.instant('campaign.status.SUSPENDED'), value: CampaignDto.StatusEnum.SUSPENDED},
            {label: this.translate.instant('campaign.status.TERMINATED'), value: CampaignDto.StatusEnum.TERMINATED},
            {label: this.translate.instant('campaign.status.ARCHIVED'), value: CampaignDto.StatusEnum.ARCHIVED},
            {label: this.translate.instant('campaign.status.DELETED'), value: CampaignDto.StatusEnum.DELETED},
        ]);
    }

    initOnUpdate() {
        const id = this.route.snapshot.paramMap.get('id');
        if (id && this.tenant) {
            this.engineCampaignService.get(this.tenant, parseInt(id)).subscribe((res) => {
                this.actual = res;

                // Block access to deleted campaigns
                if (this.actual.campaignDto.status === CampaignDto.StatusEnum.DELETED) {
                    this.messageService.add({
                        severity: 'warn',
                        summary: this.translate.instant('campaign.edit.message.accessDeniedSummary'),
                        detail: this.translate.instant('campaign.edit.message.deletedDetail')
                    });
                    this.router.navigate(['/h/marketing/campaign']);
                    return;
                }

                this.actual.campaignDto.dateEnd = this.actual.campaignDto.dateEnd ? moment(this.actual.campaignDto.dateEnd).toDate() : null;
                this.actual.campaignDto.dateStart = this.actual.campaignDto.dateStart ? moment(this.actual.campaignDto.dateStart).toDate() : null;
            });
        }
    }

    publishCampaign() {
        const updatedCampaign: CampaignDto = {
            ...this.actual.campaignDto,
            status: CampaignDto.StatusEnum.INPROGRESS
        };

        const partner = this.actual?.campaignDto?.partner;
        this.engineCampaignService.createOrUpdate(updatedCampaign, this.tenant, false, undefined, undefined, partner).subscribe(
            () => {
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('campaign.edit.message.successSummary'),
                    detail: this.translate.instant('campaign.edit.message.published')
                });
                // Redirect to listing after successful publication
                this.router.navigate(['/h/marketing/campaign']);
            },
            () => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('campaign.edit.message.errorSummary'),
                    detail: this.translate.instant('campaign.edit.message.publishFailed')
                });
            }
        );
    }

    suspendCampaign() {
        const updatedCampaign: CampaignDto = {
            ...this.actual.campaignDto,
            status: CampaignDto.StatusEnum.SUSPENDED
        };

        this.engineCampaignService.createOrUpdate(updatedCampaign, this.tenant, false).subscribe(
            () => {
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('campaign.edit.message.successSummary'),
                    detail: this.translate.instant('campaign.edit.message.suspended')
                });
                // Update the local status to reflect the change
                this.actual.campaignDto.status = CampaignDto.StatusEnum.SUSPENDED;
            },
            () => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('campaign.edit.message.errorSummary'),
                    detail: this.translate.instant('campaign.edit.message.suspendFailed')
                });
            }
        );
    }

    terminateCampaign() {
        this.confirmationService.confirm({
            message: this.translate.instant('campaign.list.dialog.terminateMessage', {name: this.actual.campaignDto.name}),
            header: this.translate.instant('campaign.list.dialog.terminateTitle'),
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                const updatedCampaign: CampaignDto = {
                    ...this.actual.campaignDto,
                    status: CampaignDto.StatusEnum.TERMINATED
                };

                const partner = this.actual?.campaignDto?.partner;
                this.engineCampaignService.createOrUpdate(updatedCampaign, this.tenant, false, undefined, undefined, partner).subscribe(
                    () => {
                        this.messageService.add({
                            severity: 'success',
                            summary: this.translate.instant('campaign.edit.message.successSummary'),
                            detail: this.translate.instant('campaign.edit.message.terminated')
                        });
                        // Update the local status to reflect the change
                        this.actual.campaignDto.status = CampaignDto.StatusEnum.TERMINATED;
                    },
                    () => {
                        this.messageService.add({
                            severity: 'error',
                            summary: this.translate.instant('campaign.edit.message.errorSummary'),
                            detail: this.translate.instant('campaign.edit.message.terminateFailed')
                        });
                    }
                );
            }
        });
    }

    deleteCampaign() {
        this.confirmationService.confirm({
            message: this.translate.instant('campaign.edit.confirm.deleteMessage', {name: this.actual.campaignDto.name}),
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                const updatedCampaign: CampaignDto = {
                    ...this.actual.campaignDto,
                    status: CampaignDto.StatusEnum.DELETED
                };

                const partner = this.actual?.campaignDto?.partner;
                this.engineCampaignService.createOrUpdate(updatedCampaign, this.tenant, false, undefined, undefined, partner).subscribe(
                    () => {
                        this.messageService.add({
                            severity: 'success',
                            summary: this.translate.instant('campaign.edit.message.successSummary'),
                            detail: this.translate.instant('campaign.edit.message.deleted')
                        });
                        // Redirect to listing after successful deletion
                        this.router.navigate(['/h/marketing/campaign']);
                    },
                    () => {
                        this.messageService.add({
                            severity: 'error',
                            summary: this.translate.instant('campaign.edit.message.errorSummary'),
                            detail: this.translate.instant('campaign.edit.message.deleteFailed')
                        });
                    }
                );
            }
        });
    }

    /** Barre de progression objectif avec repère au % seuil d'alerte (unités). */
    showUnitsAlertProgress(): boolean {
        const dto = this.actual?.campaignDto;
        return dto?.campaignMode === CampaignDto.CampaignModeEnum.UNITSMAX
            && dto.alertThresholdUnits != null
            && this.actual?.unitsAppliedRatio != null;
    }

    unitsProgressBarWidth(): number {
        const r = this.actual?.unitsAppliedRatio;
        if (r == null) {
            return 0;
        }
        return Math.min(100, Math.max(0, r));
    }

    unitsOverAlertThreshold(): boolean {
        const r = this.actual?.unitsAppliedRatio;
        const t = this.actual?.campaignDto?.alertThresholdUnits;
        if (r == null || t == null) {
            return false;
        }
        return r >= t;
    }

    /** Barre de progression budget avec repère au % seuil d'alerte (coût). */
    showBudgetAlertProgress(): boolean {
        const dto = this.actual?.campaignDto;
        return dto?.campaignMode === CampaignDto.CampaignModeEnum.BUDGETMAX
            && dto.alertThresholdCost != null
            && this.actual?.budgetConsumedRatio != null;
    }

    budgetProgressBarWidth(): number {
        const r = this.actual?.budgetConsumedRatio;
        if (r == null) {
            return 0;
        }
        return Math.min(100, Math.max(0, r));
    }

    budgetOverAlertThreshold(): boolean {
        const r = this.actual?.budgetConsumedRatio;
        const t = this.actual?.campaignDto?.alertThresholdCost;
        if (r == null || t == null) {
            return false;
        }
        return r >= t;
    }

    alertThresholdMarkerLeft(threshold: number | undefined | null): number {
        if (threshold == null) {
            return 0;
        }
        return Math.min(100, Math.max(0, threshold));
    }
}
